import { t } from '@lingui/core/macro';
import {
  UserGroupDataQualityField,
  type UserGroupDataQualityIssueRecord,
  UserGroupDataQualityProblem
} from '@navio/nsdk';

type StepKind = 'address' | 'email' | 'name' | 'phone';

type CustomerDataQualityFixStepMetadata = {
  cta: (isLastStep: boolean, willPay: boolean) => string;
  description: (isMultiStep: boolean) => string;
  field: UserGroupDataQualityField;
  step: StepKind;
  title: (isMultiStep: boolean) => string;
};

/**
 * The fix flow, in the order it walks its steps, paired with the report field
 * each step clears. Single source: the step union, order, and blocking fields
 * all derive from it.
 */
const CUSTOMER_DATA_QUALITY_FIX_STEPS = [
  {
    step: 'email',
    field: UserGroupDataQualityField.EMAIL,
    cta: (isLastStep, willPay) => {
      if (!isLastStep) {
        return t`Save email`;
      }
      return willPay ? t`Save email and pay` : t`Save email and confirm booking`;
    },
    title: (isMultiStep) => (isMultiStep ? t`Two details for your invoice` : t`One detail for your invoice`),
    description: (isMultiStep) =>
      isMultiStep
        ? t`We send the receipt by email, and need a billing address to charge the card.`
        : t`Your receipt goes to this email address.`
  },
  {
    step: 'address',
    field: UserGroupDataQualityField.INVOICE_ADDRESS,
    cta: (isLastStep, willPay) => {
      if (!isLastStep) {
        return t`Save address`;
      }
      return willPay ? t`Save address and pay` : t`Save address and confirm booking`;
    },
    title: (isMultiStep) => (isMultiStep ? t`Billing address` : t`One detail for your invoice`),
    description: (isMultiStep) =>
      isMultiStep
        ? t`Search for your address, or enter it manually.`
        : t`We need a billing address before we can charge the card.`
  },
  {
    step: 'phone',
    field: UserGroupDataQualityField.MOBILE_PHONE_NUMBER,
    cta: (isLastStep, willPay) => {
      if (!isLastStep) {
        return t`Save phone number`;
      }
      return willPay ? t`Save phone number and pay` : t`Save phone number and confirm booking`;
    },
    title: () => t`Mobile phone number`,
    description: () => t`Enter a mobile number we can use for this booking.`
  },
  {
    step: 'name',
    field: UserGroupDataQualityField.NAME,
    cta: (isLastStep, willPay) => {
      if (!isLastStep) {
        return t`Save name`;
      }
      return willPay ? t`Save name and pay` : t`Save name and confirm booking`;
    },
    title: () => t`Name`,
    description: () => t`Enter the name that should be on the booking.`
  }
] as const satisfies readonly CustomerDataQualityFixStepMetadata[];

/** One step of the sheet: what it collects, and the report field it has to clear. */
export type CustomerDataQualityFixStep = (typeof CUSTOMER_DATA_QUALITY_FIX_STEPS)[number];
export type CustomerDataQualityFixStepKind = CustomerDataQualityFixStep['step'];

const fixableFields = new Set<UserGroupDataQualityField>(CUSTOMER_DATA_QUALITY_FIX_STEPS.map(({ field }) => field));

/**
 * A duplicate name can be a shared family or company profile, and the customer
 * has no flow to resolve it. A mobile number shared by several accounts is valid.
 */
export const isIssueRelevantToCustomers = (issue: UserGroupDataQualityIssueRecord): boolean =>
  !(
    issue.problem === UserGroupDataQualityProblem.DUPLICATE &&
    (issue.field === UserGroupDataQualityField.NAME || issue.field === UserGroupDataQualityField.MOBILE_PHONE_NUMBER)
  );

/**
 * Checkout blocks only on issues the customer can correct here. Lease-contact
 * and other organization-managed lines stay on the banner, and they cannot
 * trap pay, confirm, or invoice.
 */
export const getBlockingCustomerDataQualityIssues = (
  issues: readonly UserGroupDataQualityIssueRecord[]
): UserGroupDataQualityIssueRecord[] =>
  issues.filter((issue) => isIssueRelevantToCustomers(issue) && fixableFields.has(issue.field));

/** The sheet's steps for this report, in fix order. Missing and invalid both count. */
export const getDataQualityFixSteps = (
  issues: readonly UserGroupDataQualityIssueRecord[]
): CustomerDataQualityFixStep[] => {
  const fields = new Set(getBlockingCustomerDataQualityIssues(issues).map((issue) => issue.field));
  return CUSTOMER_DATA_QUALITY_FIX_STEPS.filter(({ field }) => fields.has(field));
};

const getDataQualityFixStep = (step: CustomerDataQualityFixStepKind): CustomerDataQualityFixStep =>
  CUSTOMER_DATA_QUALITY_FIX_STEPS.find((candidate) => candidate.step === step)!;

export const getDataQualityFixStepTitle = (step: CustomerDataQualityFixStepKind, isMultiStep: boolean): string =>
  getDataQualityFixStep(step).title(isMultiStep);

export const getDataQualityFixStepDescription = (step: CustomerDataQualityFixStepKind, isMultiStep: boolean): string =>
  getDataQualityFixStep(step).description(isMultiStep);

/** The final step names the interrupted checkout action it resumes. */
export const getDataQualityFixStepCtaText = ({
  step,
  isLastStep,
  willPay
}: {
  step: CustomerDataQualityFixStepKind;
  isLastStep: boolean;
  willPay: boolean;
}): string => {
  return getDataQualityFixStep(step).cta(isLastStep, willPay);
};
